import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, BarChart3, TrendingUp, Users, Phone, Target, Clock, CheckCircle, XCircle } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { TeamLeadService } from '../../services/teamLeadService';

const LeadAnalytics = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');
  const [selectedPeriod, setSelectedPeriod] = useState('week');

  const { data: teamStatsData, isLoading } = useQuery({
    queryKey: ['teamLeadAnalytics', user?.id, selectedPeriod],
    queryFn: () => TeamLeadService.getTeamStats(user?.id),
    enabled: !!user?.id,
  });

  const { data: teamMembersData } = useQuery({
    queryKey: ['teamLeadMembers', user?.id],
    queryFn: () => TeamLeadService.getTeamMembers(user?.id),
    enabled: !!user?.id,
  });

  const teamStats = teamStatsData?.data || {};
  const teamMembers = teamMembersData?.data || [];

  const analyticsMetrics = [
    {
      label: 'Total Leads',
      value: teamStats.totalLeads || teamStats.totalLeads || 0,
      icon: Phone,
      color: 'bg-blue-100 text-blue-700',
      iconColor: 'text-blue-600',
    },
    {
      label: 'Converted Leads',
      value: teamStats.confirmed || teamStats.convertedLeads || 0,
      icon: CheckCircle,
      color: 'bg-green-100 text-green-700',
      iconColor: 'text-green-600',
    },
    {
      label: 'Conversion Rate',
      value: `${teamStats.conversionRate || 0}%`,
      icon: TrendingUp,
      color: 'bg-purple-100 text-purple-700',
      iconColor: 'text-purple-600',
    },
    {
      label: 'Booked Orders',
      value: teamStats.booked || 0,
      icon: Target,
      color: 'bg-yellow-100 text-yellow-700',
      iconColor: 'text-yellow-600',
    },
  ];

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/teamlead/more')}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft size={24} className="text-gray-700" />
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Lead Analytics</h1>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="w-16 h-16 border-4 border-indigo-500 border-t-transparent rounded-full spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate('/teamlead/more')}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft size={24} className="text-gray-700" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Lead Analytics</h1>
          <p className="text-gray-600 mt-1">Detailed lead conversion analytics</p>
        </div>
      </div>

      {/* Period Selector */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-2">
        <div className="flex space-x-2">
          {['today', 'week', 'month', 'year'].map((period) => (
            <button
              key={period}
              onClick={() => setSelectedPeriod(period)}
              className={`flex-1 px-4 py-2 rounded-lg font-medium transition ${
                selectedPeriod === period
                  ? 'bg-indigo-500 text-white'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              {period.charAt(0).toUpperCase() + period.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Analytics Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {analyticsMetrics.map((metric, index) => {
          const Icon = metric.icon;
          return (
            <div
              key={index}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${metric.color}`}>
                  <Icon size={24} className={metric.iconColor} />
                </div>
                <BarChart3 size={20} className="text-gray-400" />
              </div>
              <p className="text-3xl font-bold text-gray-900 mb-1">{metric.value}</p>
              <p className="text-sm text-gray-600">{metric.label}</p>
            </div>
          );
        })}
      </div>

      {/* Team Performance */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Team Performance</h3>
        {teamMembers.length === 0 ? (
          <div className="text-center py-8">
            <Users size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-gray-500">No team members</p>
          </div>
        ) : (
          <div className="space-y-4">
            {teamMembers.map((member) => (
              <div key={member.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                    <span className="text-indigo-600 font-bold">
                      {member.name?.charAt(0).toUpperCase() || 'T'}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{member.name || 'Unknown'}</p>
                    <p className="text-sm text-gray-600">{member.email || 'N/A'}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-6">
                  <div className="text-center">
                    <p className="text-sm text-gray-600">Leads</p>
                    <p className="font-semibold text-gray-900">{member.assignedLeads || 0}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-600">Conversion</p>
                    <p className="font-semibold text-gray-900">{member.conversionRate || 0}%</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Analytics Chart Placeholder */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Conversion Trend</h3>
        <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg">
          <p className="text-gray-500">Analytics chart visualization coming soon</p>
        </div>
      </div>
    </div>
  );
};

export default LeadAnalytics;

